/**
 * Thin, defensive network client for the shared translations ledger on the
 * baba-v2 mobile gateway (`translate.itsbaba.com/api/v1`) — mirrors the
 * mobile domain calls in baba-v2 mobile/services/api/domains.ts
 * (listTranslations / syncTranslations / setFavorite / deleteTranslation /
 * clearHistory).
 *
 * `../gateway` (apps/web/lib/gateway.ts) is owned by another agent and
 * imported lazily, same as apps/web/lib/referral-gateway.ts, so a module
 * shape change or a signed-out session there fails one call instead of this
 * whole module, and every export here can resolve to a clean failure the
 * caller falls back on instead of throwing something unexpected.
 */
import type { GatewayTranslationRow } from './gatewayLibraryMapping'

type GatewayFetchJson = <T>(path: string, init?: RequestInit) => Promise<T>

interface GatewayModule {
  gatewayFetchJson?: GatewayFetchJson
}

async function loadGateway(): Promise<GatewayModule | null> {
  try {
    return (await import('../gateway')) as GatewayModule
  } catch {
    return null
  }
}

/** Thrown when `../gateway` failed to load or does not export `gatewayFetchJson`
 * (module-shape change) — distinct from `GatewayAuthError`/`GatewayError`,
 * which `../gateway` itself throws for a missing/rejected session. */
export class GatewayLibraryUnavailableError extends Error {
  constructor() {
    super('Gateway client unavailable')
    this.name = 'GatewayLibraryUnavailableError'
  }
}

async function gatewayFetchJson(): Promise<GatewayFetchJson> {
  const mod = await loadGateway()
  if (!mod?.gatewayFetchJson) throw new GatewayLibraryUnavailableError()
  return mod.gatewayFetchJson
}

/** `GET /translations` — every non-deleted row owned by the caller, newest first. */
export async function listGatewayTranslations(
  opts: { limit?: number; platform?: string; signal?: AbortSignal } = {},
): Promise<GatewayTranslationRow[]> {
  const fetchJson = await gatewayFetchJson()
  const qs = new URLSearchParams()
  if (opts.limit) qs.set('limit', String(opts.limit))
  if (opts.platform) qs.set('platform', opts.platform)
  const q = qs.toString()
  const rows = await fetchJson<unknown>(`/translations${q ? `?${q}` : ''}`, opts.signal ? { signal: opts.signal } : {})
  // The gateway answers with a bare array; anything else is treated as empty
  // rather than letting a shape change crash the Library.
  return Array.isArray(rows) ? (rows as GatewayTranslationRow[]) : []
}

/**
 * `POST /translations/sync` — upload client rows. The server forces
 * `user_id` to the caller and whitelists columns (syncRowContract.ts), so
 * this can never write another user's rows or an unexpected column.
 */
export async function syncGatewayTranslationRows(
  rows: Record<string, unknown>[],
): Promise<{ synced: number; skipped: number }> {
  const fetchJson = await gatewayFetchJson()
  return fetchJson('/translations/sync', { method: 'POST', body: JSON.stringify({ rows }) })
}

/** Convenience wrapper for pushing exactly one row (a just-completed web translation). */
export async function syncOneGatewayTranslationRow(row: Record<string, unknown>): Promise<boolean> {
  const result = await syncGatewayTranslationRows([row])
  return result.synced > 0
}

/** `PATCH /translations/:id` — flips the `is_favorite` flag on an existing row. */
export async function setGatewayTranslationFavorite(id: string, isFavorite: boolean): Promise<boolean> {
  const fetchJson = await gatewayFetchJson()
  const res = await fetchJson<{ updated: boolean }>(`/translations/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify({ is_favorite: isFavorite }),
  })
  return !!res.updated
}

/** `DELETE /translations/:id` — soft delete. */
export async function deleteGatewayTranslation(id: string): Promise<boolean> {
  const fetchJson = await gatewayFetchJson()
  const res = await fetchJson<{ deleted: boolean }>(`/translations/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  })
  return !!res.deleted
}

/**
 * `POST /translations/clear` — soft-deletes every row the caller owns.
 *
 * Called with NO platform, so the clear is account-wide: the Library is one
 * shared ledger, and the phone now clears account-wide too
 * (baba-v2 mobile/services/syncService.ts `clearAllHistory`). Omitting the
 * field is what makes the gateway drop the platform predicate — see
 * server/gateway/data.ts `clearHistory`. The old `'web'` scoping left the
 * phone's rows behind, which read as "Clear History didn't work".
 * `platform` stays available for a deliberately per-platform caller.
 */
export async function clearGatewayHistory(platform?: string): Promise<number> {
  const fetchJson = await gatewayFetchJson()
  const res = await fetchJson<{ cleared: number }>('/translations/clear', {
    method: 'POST',
    body: JSON.stringify(platform ? { platform } : {}),
  })
  return res.cleared ?? 0
}
